/**
 * Product Modal Component - Using FormModal with Unsaved Changes Detection
 * Modal for creating and editing products with tabs for variations
 */
import React, { useState, useEffect, useRef } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';
import { FormModal } from '../../components/ui/FormModal';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { CategoryTreeSelector } from '../../components/ui/CategoryTreeSelector';
import { CurrencyInput, QuantityInput } from '../../components/form/MaskedInputs';
import { HelpTooltip, HelpAlert } from '../../components/ui/HelpGuide';
import { productSchema } from '../../application/validators/productValidators';
import { useInstance } from '../../contexts/InstanceContext';
import { toMinor } from '../../lib/currency';
import { Plus, Trash2, Package, Tags, Box, Upload, Loader2, X } from 'lucide-react';

export function ProductModal({
  open,
  onOpenChange,
  product,
  categories,
  suppliers = [],
  brands = [],
  unitsOfMeasure = [],
  onSave,
  loading,
}) {
  const { t } = useTranslation();
  const { instanceSettings, formatCurrency } = useInstance();
  const isEditing = !!product?.id;
  const [activeTab, setActiveTab] = useState('basic');
  const [imageUrl, setImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(productSchema),
    defaultValues: product || {
      name: '',
      description: '',
      price: 0,
      cost_price: 0,
      category_id: '',
      supplier_id: '',
      brand_id: '',
      unit_of_measure_id: '',
      stock: 0,
      min_stock: 5,
      max_stock: 100,
      sku: '',
      is_service: false,
      track_stock: true,
      is_active: true,
      min_stock_alert: false,
      min_stock_quantity: 10,
      variations: [],
    },
  });

  const { fields: variations, append: addVariation, remove: removeVariation } = useFieldArray({
    control,
    name: 'variations',
  });

  const trackStock = watch('track_stock');
  const minStockAlert = watch('min_stock_alert');
  const basePrice = watch('price');
  const formValues = watch(); // Watch all form values for auto-save

  // Handle draft loading
  const handleLoadDraft = (draft) => {
    reset(draft);
  };

  // Reset form when modal opens or product changes
  useEffect(() => {
    if (!open) return; // Only reset when modal is opening
    
    if (product) {
      setImageUrl(product.image_url || '');
      reset({
        ...product,
        cost_price: product.cost_price || 0,
        supplier_id: product.supplier_id || '',
        brand_id: product.brand_id || '',
        unit_of_measure_id: product.unit_of_measure_id || '',
        min_stock: product.min_stock ?? 5,
        max_stock: product.max_stock ?? 100,
        min_stock_alert: product.min_stock_alert ?? false,
        min_stock_quantity: product.min_stock_quantity ?? 10,
        variations: product.variations || [],
      });
    } else {
      setImageUrl('');
      reset({
        name: '',
        description: '',
        price: 0,
        cost_price: 0,
        category_id: '',
        supplier_id: '',
        brand_id: '',
        unit_of_measure_id: '',
        stock: 0,
        min_stock: 5,
        max_stock: 100,
        sku: '',
        is_service: false,
        track_stock: true,
        is_active: true,
        min_stock_alert: false,
        min_stock_quantity: 10,
        variations: [],
      });
    }
    setActiveTab('basic');
  }, [open, product, reset]);

  const onSubmit = (data) => {
    // Convert monetary values to minor units (cents)
    const dataWithMinor = {
      ...data,
      price_minor: toMinor(data.price),
      cost_price_minor: data.cost_price ? toMinor(data.cost_price) : null,
      // Convert variations price modifiers
      variations: data.variations?.map(v => ({
        ...v,
        price_modifier_minor: toMinor(v.price_modifier || 0),
      })) || [],
    };
    // Remove float fields (API will use _minor fields)
    delete dataWithMinor.price;
    delete dataWithMinor.cost_price;
    dataWithMinor.variations.forEach(v => delete v.price_modifier);
    
    onSave(dataWithMinor);
  };

  const handleAddVariation = () => {
    addVariation({
      id: `var_${Date.now()}`,
      name: '',
      price_modifier: 0,
      sku: '',
      is_active: true,
    });
  };

  const handleUploadImage = async (file) => {
    if (!file || !isEditing) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploadingImage(true);
      const res = await api.post(`/products/${product.id}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImageUrl(res.data.image_url);
      toast.success('Imagem do produto atualizada!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar imagem');
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const handleRemoveImage = async () => {
    if (!isEditing) return;
    try {
      await api.delete(`/products/${product.id}/image`);
      setImageUrl('');
      toast.success('Imagem removida.');
    } catch (err) {
      toast.error('Erro ao remover imagem');
    }
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleUploadImage(file);
    } else {
      toast.error('Por favor, envie um arquivo de imagem.');
    }
  };

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={isEditing ? t('products.editProduct') : t('products.newProduct')}
      description={isEditing ? 'Atualize as informações do produto' : 'Preencha os dados do novo produto'}
      size="lg"
      formId="product"
      entityId={product?.id}
      isDirty={isDirty}
      formValues={formValues}
      onLoadDraft={handleLoadDraft}
      onSave={handleSubmit(onSubmit)}
      loading={loading}
      confirmText={t('common.save')}
      cancelText={t('common.cancel')}
    >
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="basic" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Básico
          </TabsTrigger>
          <TabsTrigger value="stock" className="flex items-center gap-2">
            <Box className="h-4 w-4" />
            Estoque
          </TabsTrigger>
          <TabsTrigger value="variations" className="flex items-center gap-2">
            <Tags className="h-4 w-4" />
            Variações
            {variations.length > 0 && (
              <Badge variant="secondary" className="ml-1">{variations.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-4 mt-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">{t('products.name')} *</Label>
            <Input
              id="name"
              {...register('name')}
              placeholder="Nome do produto"
              data-testid="product-name-input"
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">{t('products.description')}</Label>
            <Textarea
              id="description"
              {...register('description')}
              placeholder="Descrição do produto"
              rows={2}
            />
          </div>

          {/* Product Image */}
          {isEditing && (
            <div className="space-y-2">
              <Label className="text-sm">Imagem do Produto</Label>
              <div className="flex items-start gap-4">
                <div
                  className="relative flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-3 hover:border-primary/50 transition-colors cursor-pointer bg-muted/30 min-w-[120px]"
                  onClick={() => !uploadingImage && imageInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleImageDrop}
                  data-testid="product-image-upload-area"
                >
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleUploadImage(e.target.files?.[0])}
                    data-testid="product-image-file-input"
                  />
                  {uploadingImage ? (
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  ) : imageUrl ? (
                    <div className="relative group">
                      <img
                        src={imageUrl}
                        alt="Produto"
                        className="w-24 h-24 object-cover rounded-lg"
                        data-testid="product-image-preview"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 rounded-lg flex items-center justify-center transition-opacity">
                        <Upload className="h-5 w-5 text-white" />
                      </div>
                    </div>
                  ) : (
                    <div className="w-24 h-24 flex flex-col items-center justify-center text-muted-foreground">
                      <Upload className="h-6 w-6 mb-1" />
                      <span className="text-xs text-center">Clique ou arraste</span>
                    </div>
                  )}
                </div>
                {imageUrl && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive mt-8"
                    onClick={handleRemoveImage}
                    data-testid="product-remove-image-btn"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Remover
                  </Button>
                )}
                {!imageUrl && (
                  <p className="text-xs text-muted-foreground mt-8">
                    Opcional. PNG, JPG (máx. 5MB).
                    <br />Salve o produto primeiro para habilitar o upload.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Price, Cost Price and Category */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="price" className="flex items-center gap-1">
                {t('products.price')} *
              </Label>
              <Controller
                name="price"
                control={control}
                render={({ field }) => (
                  <CurrencyInput
                    id="price"
                    value={field.value}
                    onValueChange={field.onChange}
                    currency={instanceSettings?.currency || 'BRL'}
                    decimalPlaces={instanceSettings?.decimal_places || 2}
                    data-testid="product-price-input"
                  />
                )}
              />
              {errors.price && (
                <p className="text-sm text-destructive">{errors.price.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="cost_price" className="flex items-center gap-1">
                Custo
                <HelpTooltip>
                  Preço de custo para cálculo de margem de lucro
                </HelpTooltip>
              </Label>
              <Controller
                name="cost_price"
                control={control}
                render={({ field }) => (
                  <CurrencyInput
                    id="cost_price"
                    value={field.value}
                    onValueChange={field.onChange}
                    currency={instanceSettings?.currency || 'BRL'}
                    decimalPlaces={instanceSettings?.decimal_places || 2}
                    data-testid="product-cost-input"
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category_id">{t('products.category')} *</Label>
              <Controller
                name="category_id"
                control={control}
                render={({ field }) => (
                  <CategoryTreeSelector
                    categories={categories}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Selecione uma categoria..."
                  />
                )}
              />
              {errors.category_id && (
                <p className="text-sm text-destructive">
                  {errors.category_id.message}
                </p>
              )}
            </div>
          </div>

          {/* Supplier, Brand, Unit of Measure */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="supplier_id">Fornecedor</Label>
              <Controller
                name="supplier_id"
                control={control}
                render={({ field }) => (
                  <Select 
                    value={field.value || "none"} 
                    onValueChange={(val) => field.onChange(val === "none" ? "" : val)}
                  >
                    <SelectTrigger data-testid="product-supplier-select">
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      {suppliers.map((sup) => (
                        <SelectItem key={sup.id} value={sup.id}>
                          {sup.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="brand_id">Marca</Label>
              <Controller
                name="brand_id"
                control={control}
                render={({ field }) => (
                  <Select 
                    value={field.value || "none"} 
                    onValueChange={(val) => field.onChange(val === "none" ? "" : val)}
                  >
                    <SelectTrigger data-testid="product-brand-select">
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhuma</SelectItem>
                      {brands.map((brand) => (
                        <SelectItem key={brand.id} value={brand.id}>
                          {brand.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="unit_of_measure_id">Unidade</Label>
              <Controller
                name="unit_of_measure_id"
                control={control}
                render={({ field }) => (
                  <Select 
                    value={field.value || "none"} 
                    onValueChange={(val) => field.onChange(val === "none" ? "" : val)}
                  >
                    <SelectTrigger data-testid="product-unit-select">
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhuma</SelectItem>
                      {unitsOfMeasure.map((unit) => (
                        <SelectItem key={unit.id} value={unit.id}>
                          {unit.name} ({unit.abbreviation})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          {/* SKU and Barcode */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sku">SKU</Label>
              <Input
                id="sku"
                {...register('sku')}
                placeholder="Código do produto"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="barcode">Código de Barras</Label>
              <Input
                id="barcode"
                {...register('barcode')}
                placeholder="EAN/UPC"
              />
            </div>
          </div>

          {/* Active Switch */}
          <Controller
            name="is_active"
            control={control}
            render={({ field }) => (
              <div className="flex items-center gap-2">
                <Switch
                  id="is_active"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <Label htmlFor="is_active">{t('common.active')}</Label>
              </div>
            )}
          />
        </TabsContent>

        {/* Stock Tab */}
        <TabsContent value="stock" className="space-y-4 mt-4">
          <HelpAlert variant="tip" title="Controle de Estoque">
            Configure os níveis de estoque para gerar alertas e ordens de compra automaticamente.
            O sistema irá sugerir compras quando o estoque atingir o mínimo.
          </HelpAlert>

          {/* Track Stock Switch */}
          <Controller
            name="track_stock"
            control={control}
            render={({ field }) => (
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div className="space-y-0.5">
                  <Label htmlFor="track_stock" className="font-medium">{t('products.trackStock')}</Label>
                  <p className="text-sm text-muted-foreground">
                    Controlar quantidade em estoque deste produto
                  </p>
                </div>
                <Switch
                  id="track_stock"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </div>
            )}
          />

          {trackStock && (
            <>
              {/* Stock Levels */}
              <div className="grid grid-cols-3 gap-4 p-4 rounded-lg border bg-muted/30">
                <div className="space-y-2">
                  <Label htmlFor="stock" className="flex items-center gap-1">
                    {t('products.stock')}
                    <HelpTooltip>Quantidade atual em estoque</HelpTooltip>
                  </Label>
                  <Controller
                    name="stock"
                    control={control}
                    render={({ field }) => (
                      <QuantityInput
                        id="stock"
                        value={field.value}
                        onValueChange={field.onChange}
                        min={0}
                        data-testid="product-stock-input"
                      />
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="min_stock" className="flex items-center gap-1">
                    Estoque Mínimo
                    <HelpTooltip>
                      Quantidade mínima para gerar alerta e ordem de compra
                    </HelpTooltip>
                  </Label>
                  <Controller
                    name="min_stock"
                    control={control}
                    render={({ field }) => (
                      <QuantityInput
                        id="min_stock"
                        value={field.value}
                        onValueChange={field.onChange}
                        min={0}
                        data-testid="product-min-stock-input"
                      />
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max_stock" className="flex items-center gap-1">
                    Estoque Máximo
                    <HelpTooltip>
                      Quantidade máxima desejada. A ordem de compra usará: (máx - atual)
                    </HelpTooltip>
                  </Label>
                  <Controller
                    name="max_stock"
                    control={control}
                    render={({ field }) => (
                      <QuantityInput
                        id="max_stock"
                        value={field.value}
                        onValueChange={field.onChange}
                        min={0}
                        data-testid="product-max-stock-input"
                      />
                    )}
                  />
                </div>
              </div>

              {/* Stock Alert Settings */}
              <div className="space-y-4 p-4 rounded-lg border bg-muted/50">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="min_stock_alert">Alerta de estoque mínimo</Label>
                    <p className="text-sm text-muted-foreground">
                      Receber notificação quando o estoque atingir o limite
                    </p>
                  </div>
                  <Controller
                    name="min_stock_alert"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        id="min_stock_alert"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="min-stock-alert-switch"
                      />
                    )}
                  />
                </div>
                
                {minStockAlert && (
                  <div className="space-y-2">
                    <Label htmlFor="min_stock_quantity">Quantidade mínima para alerta</Label>
                    <Controller
                      name="min_stock_quantity"
                      control={control}
                      render={({ field }) => (
                        <QuantityInput
                          id="min_stock_quantity"
                          value={field.value}
                          onValueChange={field.onChange}
                          min={1}
                          data-testid="min-stock-quantity-input"
                        />
                      )}
                    />
                    <p className="text-xs text-muted-foreground">
                      Alerta será disparado quando estoque estiver abaixo deste valor
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="variations" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Variações do Produto</h3>
              <p className="text-sm text-muted-foreground">
                Adicione variações como tamanhos (P, M, G) ou sabores
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddVariation}
              data-testid="add-variation-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              Adicionar
            </Button>
          </div>

          {variations.length === 0 ? (
            <div className="text-center py-8 border rounded-lg border-dashed">
              <Tags className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
              <p className="text-muted-foreground">Nenhuma variação cadastrada</p>
              <p className="text-sm text-muted-foreground">
                Clique em &quot;Adicionar&quot; para criar variações
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {variations.map((variation, index) => (
                <div
                  key={variation.id}
                  className="p-4 border rounded-lg space-y-3 bg-muted/30"
                  data-testid={`variation-item-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Variação {index + 1}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => removeVariation(index)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Nome *</Label>
                      <Input
                        {...register(`variations.${index}.name`)}
                        placeholder="Ex: Grande, Pequeno"
                        className="h-9"
                        data-testid={`variation-name-${index}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Modificador de Preço</Label>
                      <Controller
                        name={`variations.${index}.price_modifier`}
                        control={control}
                        render={({ field }) => (
                          <CurrencyInput
                            value={field.value}
                            onValueChange={field.onChange}
                            currency={instanceSettings?.currency || 'BRL'}
                            decimalPlaces={instanceSettings?.decimal_places || 2}
                            className="h-9"
                            data-testid={`variation-price-${index}`}
                          />
                        )}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">SKU</Label>
                      <Input
                        {...register(`variations.${index}.sku`)}
                        placeholder="Código"
                        className="h-9"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      Preço final:{' '}
                      <span className="font-mono font-medium text-foreground">
                        {formatCurrency(
                          (basePrice || 0) + (watch(`variations.${index}.price_modifier`) || 0)
                        )}
                      </span>
                    </span>
                    <Controller
                      name={`variations.${index}.is_active`}
                      control={control}
                      render={({ field }) => (
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            className="h-4 w-7"
                          />
                          <Label className="text-xs">Ativo</Label>
                        </div>
                      )}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Help text */}
          <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm">
            <p className="font-medium text-blue-500 mb-1">Como funciona?</p>
            <ul className="text-muted-foreground space-y-1 text-xs">
              <li>• O <strong>modificador de preço</strong> é adicionado ao preço base do produto</li>
              <li>• Exemplo: Produto R$ 10,00 + Variação &quot;Grande&quot; (+R$ 5,00) = R$ 15,00</li>
              <li>• Use valores negativos para descontos</li>
            </ul>
          </div>
        </TabsContent>
      </Tabs>
    </FormModal>
  );
}

export default ProductModal;
